import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, AlertTriangle, Lightbulb, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline AI compliance coach — click-to-run, referenced to historical patterns
 * in the user's own workspace. Non-blocking; user can submit regardless.
 */
export default function ComplianceCoach({ entityType, draft, disabled, testIdPrefix = "coach" }) {
  const [advice, setAdvice] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/coach/preview", { entity_type: entityType, draft }, { timeout: 45000 });
      setAdvice(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Coach unavailable");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-t border-slate-200 pt-4 mt-2">
      {!advice ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Sparkles className="w-3.5 h-3.5 text-[#0055FF]" />
            <span>Get an AI-assisted review of this draft before you submit.</span>
          </div>
          <Button type="button" onClick={run} disabled={loading || disabled}
                  data-testid={`${testIdPrefix}-run-btn`}
                  size="sm" variant="outline"
                  className="border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white font-semibold">
            {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Coaching…</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Ask coach</>}
          </Button>
        </div>
      ) : (
        <div className="rounded border border-[#0055FF]/30 bg-[#0055FF]/5 p-4" data-testid={`${testIdPrefix}-panel`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#0055FF]" />
              <span className="vq-overline text-[#0055FF]">AI Compliance Coach</span>
            </div>
            {typeof advice.clarity_score === "number" && (
              <span className="text-xs font-mono text-slate-700" data-testid={`${testIdPrefix}-score`}>
                Clarity: <strong>{advice.clarity_score}/100</strong>
              </span>
            )}
          </div>

          <div className="text-sm text-slate-900 font-medium mb-3" data-testid={`${testIdPrefix}-overall`}>
            {advice.overall}
          </div>

          {advice.similar_patterns && (
            <div className="text-xs text-slate-600 italic mb-3 flex items-start gap-2">
              <Lightbulb className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
              <span>{advice.similar_patterns}</span>
            </div>
          )}

          {(advice.suggestions || []).length > 0 && (
            <div className="space-y-2 mb-3">
              {advice.suggestions.map((s, i) => (
                <div key={`${s.field || "sug"}-${i}`} className="flex items-start gap-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider border shrink-0 ${
                    s.priority === "High" ? "bg-red-50 text-red-700 border-red-200" :
                    s.priority === "Medium" ? "bg-amber-50 text-amber-700 border-amber-200" :
                    "bg-emerald-50 text-emerald-700 border-emerald-200"
                  }`}>{s.priority}</span>
                  <div className="flex-1">
                    <div className="text-slate-500 font-mono text-[9px] uppercase tracking-wider mb-0.5">
                      {s.field}{s.iso_ref && ` • ${s.iso_ref}`}
                    </div>
                    <div className="text-slate-800">{s.note}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {(advice.risks || []).length > 0 && (
            <div className="border-t border-[#0055FF]/20 pt-3 mt-3">
              <div className="vq-overline text-[9px] text-slate-600 mb-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Risks to consider
              </div>
              <ul className="text-xs text-slate-700 space-y-0.5 pl-4">
                {advice.risks.map((r, i) => (<li key={`risk-${i}-${(r || "").slice(0, 24)}`} className="list-disc">{r}</li>))}
              </ul>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-[#0055FF]/20 flex items-center justify-between gap-2 flex-wrap">
            <p className="text-[10px] font-mono text-slate-500 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
              You remain solely responsible for the final record content.
            </p>
            <Button type="button" size="sm" variant="ghost" onClick={() => setAdvice(null)}
                    data-testid={`${testIdPrefix}-dismiss`} className="text-xs h-7">
              Dismiss
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
